/**
 * The row of buttons that acts on messages.
 *
 * One component for the reading pane and for the multi-selection, because they
 * are the same question asked about one message or about forty, and two
 * components would be two places for "does this account have an Archive
 * folder?" to be answered differently.
 *
 * Every control is a real `<button>`. The two that describe a state the message
 * is in rather than a thing that happens to it -- Flag and Junk -- carry
 * `aria-pressed`, so a screen reader says "flagged, pressed" instead of leaving
 * the reader to guess whether the button turns it on or reports it is on. Read
 * and unread are deliberately not a pressed toggle: the label changes instead,
 * because "mark as read, not pressed" is a sentence nobody can parse.
 *
 * A button whose target mailbox does not exist is rendered disabled with the
 * reason on it, never hidden and never live-and-failing. Hidden would leave
 * someone hunting for a feature that is present on their other account;
 * live-and-failing spends a round trip to say what the client already knew.
 */

import type { ComponentChildren } from "preact";
import { signal } from "@preact/signals";
import { useRef, useState } from "preact/hooks";

import * as actions from "../lib/actions";
import { plural } from "../lib/format";
import * as priority from "../lib/priority";
import * as reminders from "../lib/reminders";
import * as store from "../lib/store";
import type { Envelope } from "../lib/types";
import { AccountDot } from "./AccountColour";
import * as Icon from "./Icons";

/** The messages a confirmation is currently asking about. Empty when closed. */
const confirming = signal<Envelope[]>([]);

/** One thing that can be done, so the bar and the menu render from one list. */
interface Deed {
  id: string;
  icon: ComponentChildren;
  label: string;
  can: actions.Eligible;
  run: () => void;
  pressed?: boolean;
}

interface Props {
  rows: Envelope[];
  /** Reader mode: labelled buttons rather than icons alone. */
  labelled?: boolean;
  /**
   * Push the filing verbs into the menu where the bar is a phone's bottom
   * toolbar.
   *
   * Only the reader asks for this. There, the bar is what a thumb reaches for
   * while reading one message, and the things worth reaching for are the
   * replies and the two flags -- Archive, Trash and Junk are a second pass over
   * mail already read, and Trash beside Reply is a destructive button under the
   * thumb that is aiming at the common one. The multi-selection bar is the
   * opposite case: filing is the entire reason forty rows are selected, so it
   * keeps all five.
   *
   * Both sets are rendered and CSS chooses, rather than a width signal picking
   * one. `display: none` takes the hidden copy out of the accessibility tree
   * too, so nothing is announced twice, and both come off the same list below
   * so the bar and the menu cannot drift.
   *
   * Since 2026-09-06 the demotion is in two grades. Archive is demoted only on
   * the phone, as before. Trash and Junk are demoted *everywhere*: they are the
   * two verbs on this bar that take mail away, they are second-pass actions on
   * a message already read, and on the desktop they sat between Archive and
   * Flag with a mis-click's width between them. They join "Delete permanently"
   * in the menu's last section, which is now the place every destructive verb
   * lives -- Amit's call, tidying the reader toolbar.
   */
  demote?: boolean;
  /**
   * Controls that belong at the end of the bar but before the menu.
   *
   * The reader's Save-offline and Load-images buttons. They are not message
   * actions -- one is about this disk, the other about this render -- and they
   * used to sit *after* the menu for exactly that reason, which left an overflow
   * menu with things to its right. Rendered here, past the verbs and behind a
   * rule, so that the menu can be the last thing on the bar without the two
   * pretending to be verbs.
   */
  trailing?: ComponentChildren;
}

export function ActionBar({ rows, labelled = false, demote = false, trailing }: Props) {
  const every = (test: (row: Envelope) => boolean): boolean =>
    rows.length > 0 && rows.every(test);

  const allFlagged = every((r) => Boolean(r.email.keywords["$flagged"]));
  const anyUnread = rows.some((r) => !r.email.keywords["$seen"]);
  const allJunk = every((r) => {
    const junk = store.roleMailbox(r.accountId, "junk");
    return Boolean(junk && r.email.mailboxIds[junk]);
  });

  const write = actions.writable(rows);

  // The three that move a message between mailboxes. One list, rendered twice.
  const filing: Deed[] = [
    {
      id: "archive",
      icon: <Icon.Archive />,
      label: "Archive",
      can: actions.eligible(rows, "archive"),
      run: () => void actions.moveToRole(rows, "archive"),
    },
    {
      id: "trash",
      icon: <Icon.Trash />,
      label: "Move to Trash",
      can: actions.eligible(rows, "trash"),
      run: () => void actions.moveToRole(rows, "trash"),
    },
    {
      id: "junk",
      icon: <Icon.Junk />,
      label: allJunk ? "Not junk" : "Mark as junk",
      pressed: allJunk,
      can: actions.eligible(rows, allJunk ? "inbox" : "junk"),
      run: () => void actions.moveToRole(rows, allJunk ? "inbox" : "junk"),
    },
  ];

  // When demoted, only Archive keeps a place on the bar (and only on a wide
  // layout); Trash and Junk go to the menu on every layout. One list, split
  // by grade, so a verb cannot be on the bar and in the menu at once.
  const onBar = demote ? filing.filter((d) => d.id === "archive") : filing;
  const destructive = demote ? filing.filter((d) => d.id !== "archive") : [];

  return (
    <div class="msg-actions" role="group" aria-label="Message actions">
      {onBar.map((deed) => (
        <Action
          key={deed.id}
          icon={deed.icon}
          label={deed.label}
          labelled={labelled}
          pressed={deed.pressed}
          can={deed.can}
          run={deed.run}
          // Off the bar on a phone when the reader asked for that; the menu
          // below carries it there.
          extraClass={demote ? "wide-only" : undefined}
        />
      ))}
      {/* A rule between filing and state, only where filing is on the bar. */}
      {demote && onBar.length > 0 && <span class="bar-rule wide-only" aria-hidden="true" />}
      <Action
        icon={<Icon.Star />}
        label={allFlagged ? "Unflag" : "Flag"}
        labelled={labelled}
        pressed={allFlagged}
        can={write}
        run={() => void actions.markFlagged(rows, !allFlagged)}
      />
      <Action
        icon={anyUnread ? <Icon.MailOpen /> : <Icon.MailClosed />}
        label={anyUnread ? "Mark as read" : "Mark as unread"}
        labelled={labelled}
        can={write}
        run={() => void actions.markSeen(rows, anyUnread)}
      />
      {/*
        A rule before the tail of the bar whether or not anything trails: the
        menu is a section of its own -- what it opens is everything that did
        not earn a button -- and on a build with nothing to trail, a rule
        that came and went with `trailing` would make the two builds' bars
        read differently for no reason anyone could see.
      */}
      {demote && <span class="bar-rule wide-only" aria-hidden="true" />}
      {trailing}
      <RemindMenu rows={rows} />
      <MoreMenu rows={rows} filing={demote ? onBar : []} destructive={destructive} />
      {confirming.value.length > 0 && <ConfirmDelete />}
    </div>
  );
}

/**
 * One button, plus the reason it is unavailable.
 *
 * `aria-disabled` rather than `disabled`, because a `disabled` button is
 * removed from the tab order and most screen readers skip it -- so the one
 * control carrying the explanation becomes the one control a blind reader
 * cannot reach. It stays focusable and announces "Archive — unavailable: work
 * has no Archive folder", and the click is refused in code instead.
 */
function Action({
  icon,
  label,
  labelled,
  can,
  run,
  pressed,
  extraClass,
}: {
  icon: ComponentChildren;
  label: string;
  labelled: boolean;
  can: actions.Eligible;
  run: () => void;
  pressed?: boolean;
  extraClass?: string;
}) {
  const blocked = can.rows.length === 0;
  const why = can.reasons.join("; ");
  const title = blocked ? `${label} — unavailable: ${why || "nothing selected"}` : label;
  return (
    <button
      class={`${labelled ? "btn" : "icon-btn"}${blocked ? " is-disabled" : ""}${
        extraClass ? ` ${extraClass}` : ""
      }`}
      aria-disabled={blocked}
      title={title}
      aria-label={title}
      {...(pressed === undefined ? {} : { "aria-pressed": pressed })}
      onClick={() => {
        if (!blocked) run();
      }}
    >
      {icon}
      {labelled && <span>{label}</span>}
    </button>
  );
}

/**
 * Move-to-folder and the one irreversible thing, behind a disclosure.
 *
 * "Move to…" appears only when every selected message is in one account. A
 * mailbox id belongs to one account, and matching folders across accounts by
 * name would quietly assume that "Receipts" in one means the same as "Receipts"
 * in another. Archive, Trash and Junk stay on the bar precisely because they
 * are roles and can be resolved per account without guessing.
 */
function MoreMenu({
  rows,
  filing = [],
  destructive = [],
}: {
  rows: Envelope[];
  /** Bar verbs the phone layout carries here instead; `narrow-only`. */
  filing?: Deed[];
  /** Verbs that take mail away, carried here on every layout. */
  destructive?: Deed[];
}) {
  const box = useRef<HTMLDetailsElement>(null);
  const accountIds = new Set(rows.map((r) => r.accountId));
  const single = accountIds.size === 1 ? [...accountIds][0] : undefined;
  const folders = single
    ? (store.mailboxes.value.get(single) ?? []).filter(
        (m) => !rows.every((r) => r.email.mailboxIds[m.id]),
      )
    : [];
  const writable = actions.writable(rows);
  const close = () => box.current?.removeAttribute("open");

  return (
    <details class="menu" ref={box}>
      <summary title="More actions" aria-label="More actions">
        <Icon.More />
      </summary>
      <div class="menu-body" role="menu">
        {/*
          The filing verbs, on the phone layout only -- see `demote`. Marked
          `narrow-only` rather than rendered conditionally so that this and the
          bar are the same list either way and cannot disagree about what
          Archive is called or when it is available.
        */}
        {filing.length > 0 && (
          <div class="narrow-only">
            {filing.map((deed) => (
              <button
                key={deed.id}
                class="menu-item"
                role="menuitem"
                disabled={deed.can.rows.length === 0}
                title={
                  deed.can.rows.length === 0
                    ? `${deed.label} — unavailable: ${deed.can.reasons.join("; ")}`
                    : deed.label
                }
                onClick={() => {
                  close();
                  deed.run();
                }}
              >
                {deed.icon}
                <span>{deed.label}</span>
              </button>
            ))}
            <div class="menu-rule" />
          </div>
        )}
        {single ? (
          folders.length > 0 && writable.rows.length > 0 ? (
            <>
              <div class="menu-label">Move to</div>
              {folders.map((m) => (
                <button
                  key={m.id}
                  class="menu-item"
                  role="menuitem"
                  onClick={() => {
                    close();
                    void actions.moveToMailbox(rows, m.id);
                  }}
                >
                  <Icon.Folder />
                  <span>{m.name}</span>
                </button>
              ))}
              <div class="menu-rule" />
            </>
          ) : (
            <div class="menu-label">
              {writable.rows.length === 0 ? writable.reasons.join("; ") : "Nowhere else to move it"}
            </div>
          )
        ) : (
          <div class="menu-label">
            Selection spans {plural(accountIds.size, "account")} — use Archive, Trash or Junk
          </div>
        )}

        <div class="menu-rule" />
        <LowPriorityItems rows={rows} close={close} />

        <div class="menu-rule" />
        {/*
          The section every destructive verb lives in, last, and in order of
          how much each takes away: Trash and Junk are undoable from their
          folders, Delete permanently is not, and it sits at the very bottom
          where a hand aiming for the top of the menu does not pass over it.
        */}
        {destructive.map((deed) => (
          <button
            key={deed.id}
            class="menu-item"
            role="menuitem"
            disabled={deed.can.rows.length === 0}
            title={
              deed.can.rows.length === 0
                ? `${deed.label} — unavailable: ${deed.can.reasons.join("; ")}`
                : deed.label
            }
            onClick={() => {
              close();
              deed.run();
            }}
          >
            {deed.icon}
            <span>{deed.label}</span>
          </button>
        ))}
        <button
          class="menu-item danger"
          role="menuitem"
          disabled={writable.rows.length === 0}
          onClick={() => {
            close();
            confirming.value = writable.rows;
          }}
        >
          <Icon.Trash />
          <span>Delete permanently</span>
        </button>
      </div>
    </details>
  );
}

/**
 * Sending a sender to the low-priority inbox, and bringing them back.
 *
 * # Why it is here and not on the bar
 *
 * The bar is for things done to *this message*. This is done to a *sender*, and
 * it reaches every message they have ever sent -- past and future, in every
 * account. That is a much larger action than the buttons beside it, and one
 * that belongs behind a disclosure where it is read before it is pressed rather
 * than hit while aiming for Archive.
 *
 * It is also not the same shape as anything on the bar. It writes no keyword,
 * moves nothing, and nothing about it reaches the provider: the classification
 * is local to this daemon. That is the whole design and the reason the label
 * says "low priority" and never "move".
 *
 * # Only for one sender at a time
 *
 * Offered when the selection resolves to a single `From` address, and otherwise
 * replaced by a line saying why. Applying it to forty rows from thirty senders
 * would write thirty rules from one click, with one Undo between them and no
 * way to see what had been done until the settings page was opened -- which is
 * how someone silences a colleague by accident.
 *
 * # The domain option
 *
 * A second item rather than a submenu or a checkbox, because the two are
 * genuinely different decisions and the wider one deserves to be read as a
 * sentence before it is chosen. The narrow one is first and is what a plain
 * click gets: demoting a person must not demote everybody who shares their mail
 * provider.
 */
function LowPriorityItems({ rows, close }: { rows: Envelope[]; close: () => void }) {
  const address = actions.soleSender(rows);
  if (!address) {
    return (
      <div class="menu-label">
        {rows.length === 0
          ? "No message selected"
          : "Several senders selected — low priority is set one sender at a time"}
      </div>
    );
  }
  const domain = priority.domainOf(address);
  // Every row from this sender agrees, because they *are* this sender; one is
  // enough to read the verdict off.
  const alreadyLow = rows.some((r) => r.email.lowPriority);

  if (alreadyLow) {
    return (
      <button
        class="menu-item"
        role="menuitem"
        onClick={() => {
          close();
          void actions.restoreSender(address);
        }}
      >
        <Icon.Inbox />
        <span>Move {address} back to Focused</span>
      </button>
    );
  }

  return (
    <>
      <div class="menu-label">Low priority</div>
      <button
        class="menu-item"
        role="menuitem"
        onClick={() => {
          close();
          void actions.demoteSender(address, "address");
        }}
      >
        <Icon.Inbox />
        <span>{address}</span>
      </button>
      {domain && (
        <button
          class="menu-item"
          role="menuitem"
          onClick={() => {
            close();
            void actions.demoteSender(domain, "domain");
          }}
        >
          <Icon.Users />
          <span>Everyone at {domain}</span>
        </button>
      )}
    </>
  );
}

/**
 * The one dialog in this file, for the one action nothing can take back.
 *
 * Trash gets no confirmation and an Undo; this gets a confirmation and no Undo,
 * and says which of the two it is in as many words. A dialog that says "are you
 * sure?" without distinguishing them trains people to click through it.
 */
function ConfirmDelete() {
  const rows = confirming.value;
  return (
    <div class="overlay" onClick={() => (confirming.value = [])}>
      <div
        class="sheet"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-delete-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="confirm-delete-title" style={{ margin: 0 }}>
          Delete {plural(rows.length, "message")} permanently?
        </h2>
        <p class="lede">
          This asks the provider to remove {rows.length === 1 ? "it" : "them"} outright, not to move{" "}
          {rows.length === 1 ? "it" : "them"} to Trash. There is no undo.
        </p>
        <div class="row-buttons">
          <button class="btn" onClick={() => (confirming.value = [])} autofocus>
            Cancel
          </button>
          <button
            class="btn danger"
            onClick={() => {
              const doomed = rows;
              confirming.value = [];
              void actions.deleteForever(doomed);
            }}
          >
            Delete permanently
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * The bar that appears once anything is ticked.
 *
 * The count is the point of it: a bulk action is trustworthy only if the number
 * it is about to act on is in front of you when you press the button. It also
 * carries the only obvious way out of selection mode, because Escape is not
 * discoverable and a selection you cannot see how to leave is a trap.
 */
export function SelectionBar() {
  const rows = store.selectedRows();
  const list = store.rows.value;
  const all = list.length > 0 && list.every((r) => store.selection.value.has(store.keyOf(r)));

  return (
    <div class="selection-bar" role="region" aria-label="Selection">
      <button
        class="check"
        role="checkbox"
        aria-checked={all ? "true" : "mixed"}
        aria-label={all ? "Clear selection" : "Select every message in the list"}
        onClick={store.toggleSelectAll}
      >
        {all ? <Icon.Check /> : <span class="dash" aria-hidden="true" />}
      </button>
      <strong aria-live="polite">{plural(rows.length, "message")} selected</strong>
      <SpannedAccounts rows={rows} />
      <span class="spacer" />
      <ActionBar rows={rows} />
      <button class="btn ghost" onClick={store.clearSelection}>
        Done
      </button>
    </div>
  );
}

/**
 * Which accounts a selection covers, and how much of each.
 *
 * Only when it covers more than one. A selection inside a single account is a
 * fact the column header already carries, and repeating it here would put a
 * colour on a bar that is on screen for every selection -- which is how a
 * signal becomes wallpaper.
 *
 * More than one is different: it is the moment before a bulk action whose
 * results this bar already reports per account ("moved 39, one came back"), and
 * knowing that six of the twenty-three are work mail is worth the line. The
 * counts are text, so a reader who cannot see the dots loses only the shortcut.
 */
function SpannedAccounts({ rows }: { rows: Envelope[] }) {
  const counts = new Map<string, number>();
  for (const r of rows) counts.set(r.accountId, (counts.get(r.accountId) ?? 0) + 1);
  if (counts.size < 2) return null;

  return (
    <span class="selection-accounts">
      {[...counts].map(([accountId, n]) => (
        <span class="acct-chip" key={accountId}>
          <AccountDot accountId={accountId} />
          {store.accounts.value.find((a) => a.id === accountId)?.name ?? accountId} · {n}
        </span>
      ))}
    </span>
  );
}

/**
 * Remind me: bring this message back at a chosen moment, on every device.
 *
 * One message at a time, because a reminder is a note about a message and a
 * selection of forty is not one thing to be reminded of. Three presets and a
 * picker; with a reminder already set, the same choices move it, and Complete
 * and Remove sit beneath. The picker stays inside the menu rather than
 * opening a dialog: it is one field, and the menu is already the place the
 * hand is.
 *
 * Every change goes to the daemon and then asks the store to re-read the
 * list, so the chip on the message, the row's tag and the Reminders section
 * agree without waiting for the stream to say so.
 */
/**
 * The reminder choices behind a bell of their own, beside the "…" menu.
 *
 * They were a section of that menu, at the bottom of a folder list long
 * enough to scroll, so the commonest of them took a scroll to reach. The bell
 * says whether this message already has a reminder by being lit.
 */
function RemindMenu({ rows }: { rows: Envelope[] }) {
  const box = useRef<HTMLDetailsElement>(null);
  const close = () => box.current?.removeAttribute("open");
  const one = rows.length === 1 ? rows[0] : undefined;
  const existing = one ? reminders.forMessage(one.accountId, one.email.id) : undefined;
  const label = existing
    ? `Reminder · ${reminders.describe(existing, new Date())}`
    : "Remind me";
  return (
    <details class={`menu remind-menu${existing ? " is-set" : ""}`} ref={box}>
      <summary title={label} aria-label={label}>
        <Icon.Bell />
      </summary>
      <div class="menu-body" role="menu">
        <RemindItems rows={rows} close={close} />
      </div>
    </details>
  );
}

function RemindItems({ rows, close }: { rows: Envelope[]; close: () => void }) {
  const [picking, setPicking] = useState(false);
  const picked = useRef<HTMLInputElement>(null);
  if (rows.length !== 1) {
    return (
      <div class="menu-label">
        {rows.length === 0 ? "No message selected" : "Reminders are set one message at a time"}
      </div>
    );
  }
  const row = rows[0]!;
  const existing = reminders.forMessage(row.accountId, row.email.id);
  const failed = (e: unknown) => store.toast(e instanceof Error ? e.message : String(e), "error");
  const setAt = (at: Date) => {
    close();
    void reminders
      .set(row.accountId, row.email.id, at)
      .then(() => store.refreshReminders())
      .catch(failed);
  };
  const now = new Date();

  return (
    <>
      <div class="menu-label">
        {existing ? `Reminder · ${reminders.describe(existing, now)}` : "Remind me"}
      </div>
      {reminders.presets(now).map((p) => (
        <button key={p.id} class="menu-item" role="menuitem" onClick={() => setAt(p.at)}>
          <Icon.Bell />
          <span>{p.label}</span>
          <span class="menu-hint">
            {reminders.describe({ remindAt: p.at.getTime() / 1000, state: "pending" }, now)}
          </span>
        </button>
      ))}
      {picking ? (
        /*
          Committed by Set (or Enter), never by the field's own change event.
          A date-time field reports a change as soon as any one part of it is
          edited -- in WebKitGTK picking a day from its calendar is a complete
          value -- so committing on change set the reminder for 09:00 of
          whichever day was clicked, before the time could be chosen.
        */
        <form
          class="menu-item menu-time"
          onSubmit={(e) => {
            e.preventDefault();
            const value = picked.current?.value;
            const at = value ? new Date(value) : null;
            if (!at || Number.isNaN(at.getTime())) return;
            if (at.getTime() < Date.now()) {
              store.toast("Pick a time that has not passed yet", "error");
              return;
            }
            setAt(at);
          }}
        >
          <Icon.Calendar />
          <input
            ref={picked}
            type="datetime-local"
            aria-label="Remind me at"
            min={reminders.inputValue(now)}
            defaultValue={reminders.inputValue(
              existing ? new Date(existing.remindAt * 1000) : reminders.presets(now)[1]!.at,
            )}
          />
          <button type="submit" class="btn primary small">
            Set
          </button>
        </form>
      ) : (
        <button
          class="menu-item"
          role="menuitem"
          onClick={(e) => {
            e.stopPropagation();
            setPicking(true);
          }}
        >
          <Icon.Calendar />
          <span>Pick a time…</span>
        </button>
      )}
      {existing && (
        <>
          <button
            class="menu-item"
            role="menuitem"
            onClick={() => {
              close();
              void reminders
                .complete(existing.id)
                .then(() => store.refreshReminders())
                .catch(failed);
            }}
          >
            <Icon.Check />
            <span>Complete</span>
          </button>
          <button
            class="menu-item"
            role="menuitem"
            onClick={() => {
              close();
              void reminders
                .remove(existing.id)
                .then(() => store.refreshReminders())
                .catch(failed);
            }}
          >
            <Icon.Close />
            <span>Remove reminder</span>
          </button>
        </>
      )}
    </>
  );
}
